import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Divider,
  Box,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import {
  getTransfersByKey,
  updateTransfer,
  getRoomsAvailableForTemporary,
  getFloorsAvailableForFit,
  getRoomsAvailableForFit,
  getRacksAvailableForFit,
  getBinsAvailableForFit,
} from "../../services/transfer";

interface ReceiveItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ReceiveItemDialog = ({ open, onClose, onSuccess }: ReceiveItemDialogProps) => {
  const [receiveData, setReceiveData] = useState<any>({ itemId: "", floorId: "", roomId: "", rackId: "", binId: "", errors: {} });
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [transferData, setTransferData] = useState<any>(null);
  const [floors, setFloors] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [racks, setRacks] = useState<any[]>([]);
  const [bins, setBins] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false, message: "", severity: "success",
  });

  const [fetchingItem, setFetchingItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetFields = () => {
    setSelectedItem(null);
    setTransferData(null);
    setFloors([]);
    setRooms([]);
    setRacks([]);
    setBins([]);
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!receiveData.itemId) {
      resetFields();
      setReceiveData((prev: any) => ({ ...prev, errors: {} }));
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchItemById(receiveData.itemId);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [receiveData.itemId]);

  const fetchItemById = async (itemId: string) => {
    if (!itemId) {
      resetFields();
      return;
    }
    try {
      setFetchingItem(true);
      const transfer = await getTransfersByKey(itemId);
      if (!transfer) {
        setReceiveData((prev: any) => ({ ...prev, errors: { itemId: "This ID does not exist" } }));
        resetFields();
        return;
      }
      if (transfer.transferStatus === "Transfered") {
        setReceiveData((prev: any) => ({ ...prev, errors: { itemId: "This item is already transferred" } }));
        resetFields();
        return;
      }
      if (!["Picked", "In_Building", "In_Room"].includes(transfer.transferStatus)) {
        setReceiveData((prev: any) => ({ ...prev, errors: { itemId: "This item cannot be received" } }));
        resetFields();
        return;
      }

      const inventory = transfer.inventory;
      setSelectedItem(inventory);
      setTransferData(transfer);
      setReceiveData((prev: any) => ({ ...prev, floorId: "", roomId: "", rackId: "", binId: "", errors: {} }));

      if (transfer.transferStatus === "Picked") {
        const data = await getRoomsAvailableForTemporary({ length: inventory.lengthCm, width: inventory.widthCm, height: inventory.heightCm, weight: inventory.itemWeight, buildingId: transfer.destinationBuildingId || inventory.buildingId });
        setRooms(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      } else if (transfer.transferStatus === "In_Building") {
        const data = await getFloorsAvailableForFit({ length: inventory.lengthCm, width: inventory.widthCm, height: inventory.heightCm, weight: inventory.itemWeight, buildingId: transfer.destinationBuildingId || inventory.buildingId });
        setFloors(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      } else if (transfer.transferStatus === "In_Room") {
        const data = await getRacksAvailableForFit({ length: inventory.lengthCm, width: inventory.widthCm, height: inventory.heightCm, weight: inventory.itemWeight, roomId: transfer.destinationRoomId || inventory.roomId });
        setRacks(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      }
    } catch {
      setReceiveData((prev: any) => ({ ...prev, errors: { itemId: "Something went wrong" } }));
      resetFields();
    } finally {
      setFetchingItem(false);
    }
  };

  const handleFloorChange = async (floorId: string) => {
    setReceiveData((prev: any) => ({ ...prev, floorId, roomId: "" }));
    setRooms([]);
    if (selectedItem && floorId) {
      const data = await getRoomsAvailableForFit({ length: selectedItem.lengthCm, width: selectedItem.widthCm, height: selectedItem.heightCm, weight: selectedItem.itemWeight, floorId: Number(floorId) });
      setRooms(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
    }
  };

  const handleRackChange = async (rackId: string) => {
    setReceiveData((prev: any) => ({ ...prev, rackId, binId: "" }));
    setBins([]);
    if (selectedItem && rackId) {
      const data = await getBinsAvailableForFit({ length: selectedItem.lengthCm, width: selectedItem.widthCm, height: selectedItem.heightCm, weight: selectedItem.itemWeight, rackId: Number(rackId) });
      setBins(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
    }
  };

  const handleSave = async () => {
    if (!transferData) return;
    
    const errors: any = {};
    if (!receiveData.itemId) errors.itemId = "SKU/Barcode is required";
    
    if (transferData.transferStatus === "Picked" && !receiveData.roomId) {
      errors.roomId = "Transit room selection is required";
    }
    if (transferData.transferStatus === "In_Building") {
      if (!receiveData.floorId) errors.floorId = "Floor selection is required";
      if (!receiveData.roomId) errors.roomId = "Room selection is required";
    }
    if (transferData.transferStatus === "In_Room") {
      if (!receiveData.rackId) errors.rackId = "Rack selection is required";
      if (!receiveData.binId) errors.binId = "Bin selection is required";
    }
    
    if (Object.keys(errors).length > 0) {
      setReceiveData((prev: any) => ({ ...prev, errors }));
      return;
    }

    try {
      setSaving(true);
      const dto: any = { transferType: "Receive" };
      if (transferData.transferStatus === "Picked") {
        dto.destinationRoomId = Number(receiveData.roomId);
        dto.transferStatus = "In_Building";
      } else if (transferData.transferStatus === "In_Building") {
        dto.destinationFloorId = Number(receiveData.floorId);
        dto.destinationRoomId = Number(receiveData.roomId);
        dto.transferStatus = "In_Room";
      } else if (transferData.transferStatus === "In_Room") {
        dto.destinationRackId = Number(receiveData.rackId);
        dto.destinationBinId = Number(receiveData.binId);
        dto.transferStatus = "Transfered";
      }

      // Use the same key (user input) that was used for validation
      await updateTransfer(receiveData.itemId, dto);
      setSnackbar({ open: true, message: "Item received successfully", severity: "success" });
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch {
      setSnackbar({ open: true, message: "Failed to receive item", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setReceiveData({ itemId: "", floorId: "", roomId: "", rackId: "", binId: "", errors: {} });
    resetFields();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography sx={dialogTitleStyles}>Receive Item</Typography>
            <Typography sx={dialogDescriptionStyles}>Receive item to its destination</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <Typography component="label" sx={labelStyles}>SKU / Barcode <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth size="small" placeholder="Enter SKU or Barcode"
                value={receiveData.itemId}
                onChange={(e) => setReceiveData((prev: any) => ({ ...prev, itemId: e.target.value }))}
                error={!!receiveData.errors.itemId}
                helperText={receiveData.errors.itemId}
                disabled={fetchingItem}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box component="img" src="/scan.svg" alt="scan" sx={{ width: 20, height: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={formFieldStyles}
              />
            </Grid>

            {fetchingItem && (
              <Grid item xs={6}>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            )}

            {!fetchingItem && transferData?.transferStatus === "Picked" && (
              <Grid item xs={6}>
                <Typography component="label" sx={labelStyles}>Transit Room <span style={{ color: "red" }}>*</span></Typography>
                <TextField select fullWidth size="small" 
                  value={receiveData.roomId}
                  onChange={(e) => setReceiveData((prev: any) => ({ ...prev, roomId: e.target.value }))}
                  error={!!receiveData.errors.roomId}
                  helperText={receiveData.errors.roomId}
                  SelectProps={{ displayEmpty: true }}
                  sx={formFieldStyles}>
                  <MenuItem value="" disabled>Select Transit Room</MenuItem>
                  {rooms.map((r: any) => (
                    <MenuItem key={r.id} value={String(r.id)}>{r.roomName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {!fetchingItem && transferData?.transferStatus === "In_Building" && (
              <>
                <Grid item xs={6}>
                  <Typography component="label" sx={labelStyles}>Floor <span style={{ color: "red" }}>*</span></Typography>
                  <TextField select fullWidth size="small" 
                    value={receiveData.floorId}
                    onChange={(e) => handleFloorChange(e.target.value)}
                    error={!!receiveData.errors.floorId}
                    helperText={receiveData.errors.floorId}
                    SelectProps={{ displayEmpty: true }}
                    sx={formFieldStyles}>
                    <MenuItem value="" disabled>Select Floor</MenuItem>
                    {floors.map((f: any) => (
                      <MenuItem key={f.id} value={String(f.id)}>{f.floorName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <Typography component="label" sx={labelStyles}>Room <span style={{ color: "red" }}>*</span></Typography>
                  <TextField select fullWidth size="small" 
                    value={receiveData.roomId}
                    onChange={(e) => setReceiveData((prev: any) => ({ ...prev, roomId: e.target.value }))}
                    error={!!receiveData.errors.roomId}
                    helperText={receiveData.errors.roomId}
                    SelectProps={{ displayEmpty: true }}
                    sx={formFieldStyles}>
                    <MenuItem value="" disabled>Select Room</MenuItem>
                    {rooms.map((r: any) => (
                      <MenuItem key={r.id} value={String(r.id)}>{r.roomName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {!fetchingItem && transferData?.transferStatus === "In_Room" && (
              <>
                <Grid item xs={6}>
                  <Typography component="label" sx={labelStyles}>Rack <span style={{ color: "red" }}>*</span></Typography>
                  <TextField select fullWidth size="small" 
                    value={receiveData.rackId}
                    onChange={(e) => handleRackChange(e.target.value)}
                    error={!!receiveData.errors.rackId}
                    helperText={receiveData.errors.rackId}
                    SelectProps={{ displayEmpty: true }}
                    sx={formFieldStyles}>
                    <MenuItem value="" disabled>Select Rack</MenuItem>
                    {racks.map((r: any) => (
                      <MenuItem key={r.id} value={String(r.id)}>{r.rackName || `Rack ${r.id}`}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <Typography component="label" sx={labelStyles}>Bin <span style={{ color: "red" }}>*</span></Typography>
                  <TextField select fullWidth size="small" 
                    value={receiveData.binId}
                    onChange={(e) => setReceiveData((prev: any) => ({ ...prev, binId: e.target.value }))}
                    error={!!receiveData.errors.binId}
                    helperText={receiveData.errors.binId}
                    SelectProps={{ displayEmpty: true }}
                    sx={formFieldStyles}>
                    <MenuItem value="" disabled>Select Bin</MenuItem>
                    {bins.map((b: any) => (
                      <MenuItem key={b.id} value={String(b.id)}>{`${b.barcode}`}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: "space-between" }}>
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !selectedItem} sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled"
          sx={{ width: "100%", backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`, color: "white", "& .MuiAlert-icon": { color: "white" }, "& .MuiAlert-action": { color: "white" } }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
export default ReceiveItemDialog;
